import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useNumbers } from '../context/NumberContext';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

// Calculate window status purely from last_inbound_ts (already fetched with contacts)
function calcWindowStatus(lastInboundTs) {
  if (!lastInboundTs) return { windowOpen: false, hoursRemaining: 0, minutesRemaining: 0 };
  const nowTs = Math.floor(Date.now() / 1000);
  const elapsed = nowTs - parseInt(lastInboundTs);
  const windowSeconds = 24 * 3600;
  const open = elapsed < windowSeconds;
  const secsLeft = open ? windowSeconds - elapsed : 0;
  return {
    windowOpen: open,
    hoursRemaining: parseFloat((secsLeft / 3600).toFixed(2)),
    minutesRemaining: Math.floor(secsLeft / 60),
  };
}

function WindowBadge({ lastInboundTs, compact }) {
  const [status, setStatus] = useState(() => calcWindowStatus(lastInboundTs));

  // Recalculate every minute so badge stays live
  useEffect(() => {
    setStatus(calcWindowStatus(lastInboundTs));
    const interval = setInterval(() => setStatus(calcWindowStatus(lastInboundTs)), 60000);
    return () => clearInterval(interval);
  }, [lastInboundTs]);

  if (!lastInboundTs) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10, fontFamily: 'var(--mono)',
        color: 'var(--text3)', background: 'var(--bg3)',
        border: '1px solid var(--border)', borderRadius: 10,
        padding: compact ? '1px 6px' : '2px 7px',
        whiteSpace: 'nowrap',
      }}>
        ⚫ No msgs
      </span>
    );
  }

  if (!status.windowOpen) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10, fontFamily: 'var(--mono)',
        color: 'var(--danger)', background: 'rgba(239,68,68,0.12)',
        border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10,
        padding: compact ? '1px 6px' : '2px 7px',
        whiteSpace: 'nowrap',
      }}>
        🔴 Closed
      </span>
    );
  }

  const h = Math.floor(status.hoursRemaining);
  const m = status.minutesRemaining % 60;
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 10, fontFamily: 'var(--mono)',
      color: 'var(--accent)', background: 'rgba(37,211,102,0.12)',
      border: '1px solid rgba(37,211,102,0.2)', borderRadius: 10,
      padding: compact ? '1px 6px' : '2px 7px',
      whiteSpace: 'nowrap',
    }}>
      🟢 {label}
    </span>
  );
}

export default function Inbox() {
  const { activeNumber } = useNumbers();
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConv, setShowConv] = useState(false);
  const [windowStatus, setWindowStatus] = useState(null); // for selected contact
  const bottomRef = useRef();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const loadContacts = useCallback(async () => {
    try {
      const res = await api.get('/messages/contacts' + (activeNumber ? '?wa_number_id=' + activeNumber.id : ''));
      setContacts(res.data);
    } catch (e) {}
    setLoading(false);
  }, [activeNumber]);

  const loadConversation = useCallback(async (waId) => {
    try {
      const res = await api.get(`/messages/conversation/${waId}${activeNumber ? '?wa_number_id=' + activeNumber.id : ''}`);
      setConversation(res.data.messages || []);
    } catch (e) {}
  }, [activeNumber]);

  useEffect(() => {
    setContacts([]);
    setSelected(null);
    setConversation([]);
    loadContacts();
    const i = setInterval(loadContacts, 2000);
    return () => clearInterval(i);
  }, [loadContacts]);

  useEffect(() => {
    if (!selected) return;
    loadConversation(selected.wa_id);
    const i = setInterval(() => loadConversation(selected.wa_id), 2000);
    return () => clearInterval(i);
  }, [selected, loadConversation]);

  // Keep window status for selected contact in sync with contacts list
  useEffect(() => {
    if (!selected) { setWindowStatus(null); return; }
    const c = contacts.find(c => c.wa_id === selected.wa_id);
    const ts = c?.last_inbound_ts ?? selected.last_inbound_ts ?? null;
    setWindowStatus(calcWindowStatus(ts));
  }, [selected, contacts]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const selectContact = (c) => {
    setSelected(c);
    if (isMobile) setShowConv(true);
  };

  const backToContacts = () => {
    setShowConv(false);
    setSelected(null);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    setSending(true);
    try {
      await api.post('/messages/send/text', { wa_number_id: activeNumber?.id, to: selected.wa_id, text: replyText });
      setReplyText('');
      loadConversation(selected.wa_id);
    } catch (e) {
      alert(e.response?.data?.error || 'Send failed');
    }
    setSending(false);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(Number(ts) * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatLastSeen = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    return d.toLocaleDateString();
  };

  const avatar = (name) => (name || '?').charAt(0).toUpperCase();

  const avatarColor = (waId) => {
    const colors = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#00BCD4'];
    return colors[(waId || '').charCodeAt(0) % colors.length];
  };

  const showContactsList = !isMobile || !showConv;
  const showConversation = !isMobile || showConv;

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* Contacts Panel */}
      {showContactsList && (
        <div style={{
          width: isMobile ? '100%' : 280,
          borderRight: isMobile ? 'none' : '1px solid var(--border)',
          background: 'var(--bg2)',
          display: 'flex', flexDirection: 'column',
          flexShrink: 0,
        }}>
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text2)', letterSpacing: '0.05em' }}>
              CONTACTS {contacts.length > 0 && `(${contacts.length})`}
            </div>
            {loading && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Loading...</span>}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {contacts.length === 0 && !loading && (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)' }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
                <div style={{ fontSize: 13 }}>No contacts yet</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Waiting for messages...</div>
              </div>
            )}
            {contacts.map(c => (
              <div
                key={c.wa_id}
                onClick={() => selectContact(c)}
                style={{
                  padding: '10px 16px',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  background: !isMobile && selected?.wa_id === c.wa_id ? 'var(--accent-dim)' : 'transparent',
                  borderLeft: !isMobile && selected?.wa_id === c.wa_id ? '3px solid var(--accent)' : '3px solid transparent',
                  display: 'flex', gap: 10, alignItems: 'center',
                  transition: 'background 0.15s',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width: 38, height: 38, borderRadius: '50%',
                  background: avatarColor(c.wa_id),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 700, color: '#000',
                  flexShrink: 0,
                }}>
                  {avatar(c.name)}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name || c.wa_id}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>
                      {formatLastSeen(c.last_seen)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 3, gap: 6 }}>
                    <div style={{
                      fontSize: 11, color: 'var(--text2)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                    }}>
                      {c.last_message || c.wa_id}
                    </div>
                    <WindowBadge lastInboundTs={c.last_inbound_ts} compact />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversation Panel */}
      {showConversation && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--text3)' }}>
              <div style={{ fontSize: 48 }}>💬</div>
              <div style={{ fontSize: 14 }}>Select a contact to start chatting</div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div style={{
                padding: '10px 16px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg2)',
                display: 'flex', alignItems: 'center', gap: 12,
                flexShrink: 0,
              }}>
                {isMobile && (
                  <button className="btn-ghost" onClick={backToContacts} style={{ padding: '6px 8px', fontSize: 18 }}>
                    ←
                  </button>
                )}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: avatarColor(selected.wa_id),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 700, color: '#000', flexShrink: 0,
                }}>
                  {avatar(selected.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{selected.name || selected.wa_id}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', fontFamily: 'var(--mono)' }}>+{selected.wa_id}</div>
                </div>
                {/* Window status in header */}
                {windowStatus && (
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <WindowBadge lastInboundTs={contacts.find(c => c.wa_id === selected.wa_id)?.last_inbound_ts ?? selected.last_inbound_ts} />
                    {!windowStatus.windowOpen && (
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Template only</div>
                    )}
                  </div>
                )}
              </div>

              {/* Window closed warning banner */}
              {windowStatus && !windowStatus.windowOpen && (
                <div style={{
                  padding: '8px 16px',
                  background: 'rgba(239,68,68,0.08)',
                  borderBottom: '1px solid rgba(239,68,68,0.2)',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12, color: 'var(--danger)',
                  flexShrink: 0,
                }}>
                  <span>⚠️</span>
                  <span>
                    <strong>24h window closed.</strong> Free-form messages won't go through —{' '}
                    <button
                      onClick={() => navigate(`/send?to=${selected.wa_id}&mode=template`)}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--danger)', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--sans)' }}
                    >
                      send a template
                    </button>{' '}
                    to re-engage.
                  </span>
                </div>
              )}

              {/* Messages */}
              <div style={{
                flex: 1, overflowY: 'auto',
                padding: '16px',
                display: 'flex', flexDirection: 'column', gap: 6,
                background: 'var(--bg)',
              }}>
                {conversation.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, marginTop: 40 }}>
                    No messages yet
                  </div>
                )}
                {conversation.map((msg, i) => {
                  const isOut = msg.direction === 'outbound';
                  return (
                    <div key={msg.id || i} style={{ display: 'flex', justifyContent: isOut ? 'flex-end' : 'flex-start' }}>
                      <div style={{
                        maxWidth: '72%',
                        padding: '8px 12px',
                        borderRadius: isOut ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: isOut ? 'var(--accent2)' : 'var(--bg3)',
                        border: isOut ? 'none' : '1px solid var(--border)',
                        color: isOut ? '#fff' : 'var(--text)',
                      }}>
                        <div style={{ fontSize: 14, lineHeight: 1.45, wordBreak: 'break-word' }}>
                          {msg.content || `[${msg.type}]`}
                        </div>
                        <div style={{
                          fontSize: 10, marginTop: 4,
                          color: isOut ? 'rgba(255,255,255,0.55)' : 'var(--text3)',
                          textAlign: 'right',
                          display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center',
                        }}>
                          {formatTime(msg.timestamp)}
                          {isOut && (
                            <span style={{ fontSize: 11 }}>
                              {msg.status === 'delivered' || msg.status === 'read' ? '✓✓' : '✓'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Reply Box */}
              <div style={{
                padding: '10px 12px',
                borderTop: '1px solid var(--border)',
                background: 'var(--bg2)',
                display: 'flex', gap: 8, alignItems: 'flex-end',
                flexShrink: 0,
              }}>
                {windowStatus && !windowStatus.windowOpen ? (
                  /* Window closed — show template button instead of text box */
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: 'var(--text3)', flex: 1 }}>
                      Window closed — can't send free-form text
                    </span>
                    <a
                      onClick={(e) => { e.preventDefault(); navigate(`/send?to=${selected.wa_id}&mode=template`); }}
                      href={`/send?to=${selected.wa_id}&mode=template`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '9px 16px', borderRadius: 'var(--r-sm)',
                        background: 'var(--bg3)', border: '1px solid var(--border)',
                        color: 'var(--text)', fontSize: 13, fontWeight: 500,
                        textDecoration: 'none', whiteSpace: 'nowrap',
                        transition: 'border-color 0.15s', cursor: 'pointer',
                      }}
                    >
                      📋 Send Template
                    </a>
                  </div>
                ) : (
                  <>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      placeholder="Type a message…"
                      rows={1}
                      style={{
                        flex: 1, resize: 'none',
                        fontFamily: 'var(--sans)',
                        maxHeight: 120,
                        overflowY: 'auto',
                        lineHeight: 1.5,
                        padding: '10px 14px',
                      }}
                    />
                    <button
                      className="btn-primary"
                      onClick={sendReply}
                      disabled={sending || !replyText.trim()}
                      style={{ padding: '10px 16px', alignSelf: 'flex-end', borderRadius: '50%', width: 42, height: 42 }}
                    >
                      {sending ? '…' : '↑'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
